import { Link } from 'react-router-dom';

export interface LineupNameItem {
  readonly name: string;
  /** When present, the name links to /program#artist-<id> (the Program page scrolls to it). */
  readonly id?: string;
}

export interface LineupNamesProps {
  items: readonly LineupNameItem[];
  /** Typography + colour for the whole run (font, size, leading, ink). */
  className?: string;
  /** Shown (muted) in place of the list when there are no items, e.g. "Navnene annonceres snart".
   *  When omitted, an empty list renders nothing. */
  emptyLabel?: string;
}

/**
 * A flowing, wrapping run of names separated by "\". The "\" is a trailing separator glued to the
 * preceding name (spacing via padding, not a breakable space) and the only breakable space sits
 * after it — so a line may end with "\" but never starts with one. Names wrap at their own spaces
 * when too wide for the line, rather than overflowing.
 *
 * Names with an `id` link to /program#artist-<id> (an inline <a>, so the "\" stays glued to it).
 * Shared by the hero, the line-up strip and the "Navnene på plakaten" section so they all render
 * identically.
 */
export function LineupNames({ items, className = '', emptyLabel }: LineupNamesProps) {
  if (items.length === 0) {
    return emptyLabel ? <p className={`${className} opacity-50`}>{emptyLabel}</p> : null;
  }

  return (
    <p className={className}>
      {items.flatMap((item, i) => {
        const label = item.id ? (
          <Link key={`n${i}`} to={`/program#artist-${item.id}`} className="hover:text-mf-coral transition-colors">
            {item.name}
          </Link>
        ) : (
          <span key={`n${i}`}>{item.name}</span>
        );
        const sep = i < items.length - 1
          ? <span key={`s${i}`} aria-hidden="true" className="px-[0.2em] opacity-60">{'\\'}</span>
          : null;
        return i > 0 ? [' ', label, sep] : [label, sep];
      })}
    </p>
  );
}
